import { Injectable } from '@angular/core';
import { IDateRangePickerConfiguration } from './date-range-picker.interface';

export interface IPickerPositioningParams {
  hostElement: HTMLElement;
  /** The picker dropdown container (`.md-drppicker`). */
  container: HTMLElement;
  configuration: IDateRangePickerConfiguration;
  resolvedDrops: 'up' | 'down';
  /** Whether the picker is still open — used to self-detach the reposition listeners. */
  isShown: () => boolean;
}

const VIEWPORT_MARGIN = 8;
const FIELD_GAP = 2;
const DIALOG_SELECTOR = 'mat-dialog-container, .mat-mdc-dialog-container';

/**
 * Positions the `ngx-daterangepicker-material` dropdown. The library renders it as
 * `position: absolute`, which gets clipped by a dialog's scrollable content; inside a dialog this
 * service re-anchors it as `position: fixed` so it escapes the overflow. Outside a dialog the legacy
 * `forceFit` behaviour is kept.
 */
@Injectable({ providedIn: 'root' })
export class DateRangePickerPositioningService {
  /** Applies positioning and returns a cleanup function for the reposition listeners. */
  public apply(params: IPickerPositioningParams): () => void {
    const { hostElement, container, configuration } = params;

    if (configuration.zoom) {
      container.style.zoom = String(configuration.zoom);
    }

    if (!hostElement.closest(DIALOG_SELECTOR)) {
      this.applyForceFit(container, configuration);

      return () => {
        // no listeners to detach outside a dialog
      };
    }

    this.positionFixed(params);

    let cleaned = false;
    const cleanup = (): void => {
      if (cleaned) {
        return;
      }

      cleaned = true;
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition, true);
    };

    const reposition = (): void => {
      if (!params.isShown()) {
        cleanup();

        return;
      }

      this.positionFixed(params);
    };

    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition, true);

    return cleanup;
  }

  private applyForceFit(container: HTMLElement, configuration: IDateRangePickerConfiguration): void {
    if (!configuration.forceFit) {
      return;
    }

    const topValue: number = parseFloat(container.style.top);
    const calculatedTopValue: number = Math.sign(topValue) * (Math.abs(topValue) - (container.clientHeight ?? 0) / 2);
    container.style.top = `${calculatedTopValue}px`;
  }

  private positionFixed({ hostElement, container, configuration, resolvedDrops }: IPickerPositioningParams): void {
    const fieldElement: HTMLElement | null = hostElement.querySelector('.mat-mdc-text-field-wrapper');

    if (!fieldElement) {
      return;
    }

    const fieldRect = fieldElement.getBoundingClientRect();
    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;

    const top = resolvedDrops === 'up' ? fieldRect.top - containerHeight - FIELD_GAP : fieldRect.bottom + FIELD_GAP;

    const { opens } = configuration;
    let leftPosition: number;

    if (opens === 'left') {
      leftPosition = fieldRect.right - containerWidth;
    } else if (opens === 'center') {
      leftPosition = fieldRect.left + fieldRect.width / 2 - containerWidth / 2;
    } else {
      leftPosition = fieldRect.left;
    }

    const clampedLeft = Math.max(
      VIEWPORT_MARGIN,
      Math.min(leftPosition, window.innerWidth - containerWidth - VIEWPORT_MARGIN),
    );
    const clampedTop = Math.max(VIEWPORT_MARGIN, Math.min(top, window.innerHeight - containerHeight - VIEWPORT_MARGIN));

    container.style.position = 'fixed';
    container.style.top = `${clampedTop}px`;
    container.style.left = `${clampedLeft}px`;
    container.style.right = 'auto';
    container.style.bottom = 'auto';
    container.style.marginTop = '0';
  }
}
